/**
 * CorrectionModal — behavioral port of the @smartbox/ui web component.
 *
 * Allows editing a committed FactRecord's value or category and submitting
 * the correction via POST /api/sb/facts/:factId/correct.
 *
 * CRITICAL ORDERING: correctFact applies to a COMMITTED FactRecord.
 * The factId comes from CommitResult.committedRecords[n].id, which is only
 * available after confirmFile() has succeeded. This modal must be opened
 * with data from the CommitResult, not the pre-confirmation FileResult.
 *
 * - `value` is edited as a raw JSON string so any JSON-serializable fact
 *   value can be corrected (numbers, objects, strings). Invalid JSON is
 *   rejected client-side before the network call.
 * - `category` is optional; leave blank to preserve the existing category.
 * - `reason` is required (CorrectFactRequest.reason is min(1)).
 *
 * The audit row is written server-side before the live update
 * (smartboxRouter.ts / SmartBox correctFact behavior).
 *
 * NEVER imports @smartbox/ui (mobile CLAUDE.md invariant 1).
 *
 * WO-M0 — design-system refactor. Card = sheet surface, Field = inputs,
 * primary Button = save, secondary = cancel, danger = discard.
 */

import { useState } from 'react';
import {
  Modal,
  View,
  ScrollView,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import type { FactRecord } from '@beam/api-types';
import { correctFact } from '../../api/smartbox';
import { ApiRequestError } from '../../api/client';
import { Card, Text, Button, Field } from '../ui';
import { theme } from '../../theme';

interface Props {
  visible: boolean;
  fact: FactRecord | null;
  onClose: () => void;
  /** Called after a successful correction so the parent can refresh state. */
  onCorrected: (factId: string) => void;
}

export function CorrectionModal({ visible, fact, onClose, onCorrected }: Props) {
  const [valueJson, setValueJson] = useState('');
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form state when the modal opens with a new fact.
  function handleOpen() {
    if (fact) {
      setValueJson(
        fact.value !== undefined ? JSON.stringify(fact.value, null, 2) : '',
      );
      setCategory('');
      setReason('');
      setError(null);
    }
  }

  async function handleSubmit() {
    if (!fact) return;

    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setError('A reason is required for corrections.');
      return;
    }

    // Build the patch — only include fields that the user actually changed.
    const patch: { value?: unknown; category?: string } = {};

    const trimmedValueJson = valueJson.trim();
    if (trimmedValueJson) {
      try {
        patch.value = JSON.parse(trimmedValueJson);
      } catch {
        setError('Value must be valid JSON. Use quotes for strings, e.g. "text".');
        return;
      }
    }

    const trimmedCategory = category.trim();
    if (trimmedCategory) {
      patch.category = trimmedCategory;
    }

    if (!patch.value && !patch.category) {
      setError('Change at least one field (value or category).');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      await correctFact(fact.id, patch, trimmedReason);
      onCorrected(fact.id);
      onClose();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Correction failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onShow={handleOpen}
      onRequestClose={onClose}
      accessibilityViewIsModal
    >
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.headerRow}>
            <Text variant="subhead">Correct Fact</Text>
            <Button
              title="Cancel"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={onClose}
              accessibilityLabel="Cancel correction"
            />
          </View>

          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
          >
            {fact ? (
              <>
                {/* Fact metadata (read-only) — Card as elevated surface */}
                <Card>
                  <Text variant="label" color="secondary">Fact ID</Text>
                  {/* IDs are measured data → data variant */}
                  <Text variant="data" selectable>{fact.id}</Text>

                  <Text variant="label" color="secondary">Current category</Text>
                  <Text variant="body">{fact.category}</Text>

                  <Text variant="label" color="secondary">Current value</Text>
                  {/* Values are measured data → data variant */}
                  <Text variant="data" numberOfLines={4}>
                    {JSON.stringify(fact.value, null, 2)}
                  </Text>

                  {fact.confidence !== undefined && (
                    <>
                      <Text variant="label" color="secondary">Confidence</Text>
                      {/* Confidence % is a measured quantity → data variant */}
                      <Text variant="data">
                        {(fact.confidence * 100).toFixed(0)}%
                      </Text>
                    </>
                  )}
                </Card>

                {/* Note about ordering */}
                <Card style={styles.noteCard}>
                  <Text variant="small" color="secondary">
                    Corrections apply to confirmed facts. The server writes an
                    audit record before updating the live value.
                  </Text>
                </Card>

                {/* New value (JSON) */}
                <Field
                  label="New value (JSON)"
                  value={valueJson}
                  onChangeText={setValueJson}
                  multiline
                  numberOfLines={4}
                  placeholder='e.g. "New Supplier LLC" or {"amount":42.00}'
                  autoCapitalize="none"
                  autoCorrect={false}
                  accessibilityLabel="New fact value as JSON"
                  editable={!loading}
                  style={styles.textAreaStyle}
                  textAlignVertical="top"
                />

                {/* New category (optional) */}
                <Field
                  label="New category (optional)"
                  value={category}
                  onChangeText={setCategory}
                  placeholder="Leave blank to keep existing"
                  autoCapitalize="none"
                  autoCorrect={false}
                  accessibilityLabel="New fact category"
                  editable={!loading}
                  returnKeyType="next"
                />

                {/* Reason (required) */}
                <Field
                  label="Reason (required)"
                  value={reason}
                  onChangeText={setReason}
                  placeholder="Why is this correction needed?"
                  autoCorrect={false}
                  accessibilityLabel="Reason for correction"
                  editable={!loading}
                  returnKeyType="done"
                  onSubmitEditing={handleSubmit}
                  error={error ?? undefined}
                />

                {/* Save = primary, cancel = secondary (per HARD RULE §3) */}
                <Button
                  title="Submit Correction"
                  variant="primary"
                  size="lg"
                  loading={loading}
                  disabled={loading}
                  onPress={handleSubmit}
                  accessibilityLabel="Submit correction"
                />
              </>
            ) : (
              <Text variant="body" color="secondary">No fact selected.</Text>
            )}
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.color.bg.app,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.spacing.lg,
    backgroundColor: theme.color.bg.surface,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: theme.layout.screenPadding,
    gap: theme.spacing.md,
    paddingBottom: theme.spacing['4xl'],
  },
  noteCard: {
    backgroundColor: theme.status.sent.bg,
    borderColor: theme.status.sent.bg,
  },
  textAreaStyle: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
});
